import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * A deploy replaces the hashed chunks and prunes the old ones, so a tab that was
 * opened before the deploy fails to import a lazy route. CloudFront answers the
 * missing asset with index.html and status 200, which webpack cannot parse.
 * Without a boundary React unmounts the whole tree and the user sees a blank page.
 */
export function isChunkLoadError(error: Error | null): boolean {
  if (!error) {
    return false;
  }
  const message = error.message || "";
  return (
    error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Loading chunk/i.test(message) ||
    /Unexpected token '<'/.test(message) ||
    /expected expression, got '<'/i.test(message)
  );
}

class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // Keep the details in the console for support; the UI stays actionable.
    console.error("Unhandled application error", error);
  }

  private reload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    const outdated = isChunkLoadError(error);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white shadow rounded-lg p-6 text-center">
          <h1 className="text-lg font-medium text-gray-900">
            {outdated ? "A new version is available" : "Something went wrong"}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {outdated
              ? "This page was loaded before the latest update. Reload to continue."
              : "The page could not be displayed. Reloading usually helps."}
          </p>
          <button
            type="button"
            onClick={this.reload}
            className="mt-4 inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 active:bg-indigo-700 transition duration-150 ease-in-out"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export { AppErrorBoundary };
